import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Inject,
  inject,
  Optional,
  signal,
  ViewChild,
} from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { DeleteModuleRightsDirective } from '../../../core/auth/directives/can-delete-module.directive';
import { TableComponent } from '../table.component';
import { TitleService } from '../../services/title.service';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation';
import type { Entry, Relation, RelationItem } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UtilityService } from '../../services/utility.service';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { MatTooltip } from '@angular/material/tooltip';
import { catchError, of, takeUntil } from 'rxjs';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { UserStore } from 'app/core/services/user.store';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Sort } from '@angular/material/sort';
import { TableColumn, Types } from '../../interfaces/table.models';
import { EntriesLoadErrorAlertComponent } from '../entries-load-error-alert/entries-load-error-alert.component';
import { entryDisplayName, LinkageEntriesSearch } from './services/linkage-entries-search';

class DialogData {}

@Component({
  selector: 'formly-linkage-items',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4">
      <mat-form-field class="fuse-mat-dense w-full pb-0" appearance="outline" floatLabel="always">
        <mat-label>{{ props.addText ?? props.label + ' toevoegen' }}</mat-label>
        <input
          matInput
          type="text"
          #searchInput
          [placeholder]="linkedEntryId() && canMutate() ? searchPlaceholder() : ''"
          [readonly]="!canMutate()"
          [formControl]="searchControl"
          [matAutocomplete]="auto"
          data-cy="linkage-items_search-input"
          (focus)="onSearchFocus()" />
        @if (isSearching()) {
          <mat-spinner matSuffix diameter="20"></mat-spinner>
        }
        <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selectEntry($event)">
          @for (option of filteredSearchResults(); track option.value.id) {
            <mat-option [value]="option.value">{{ option.label }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
      <!-- A 403 on the search must not read as "no results" — see entries-load-error.ts. -->
      @if (loadError()) {
        <app-entries-load-error-alert class="my-4 clear" [error]="loadError()!" [label]="props.label" dataCyPrefix="linkage-items" />
      }
      @if (!linkedEntryId()) {
        <fuse-alert class="my-4 clear" [type]="'warning'">
          Je kan pas {{ props.label }} toevoegen als je huidige item hebt opgeslagen.
        </fuse-alert>
      }
      <app-table
        class="flex-1 w-full"
        [list]="displayEntries()"
        [searchTerm]="''"
        [showbulkActions]="false"
        [customizeColumnsOption]="false"
        [defaultColumns]="columns()"
        [tableId]="linkedModule()?.id"
        [moduleId]="linkedModule()?.id"
        (sortChange)="onSortChange($event)">
        <ng-template #actionsTemplate let-entry="entry">
          @if (canMutate()) {
            <button
              *canDeleteModule="{ moduleId: linkedModule()?.id, show: true }"
              mat-icon-button
              type="button"
              class="text-accent hover:text-warn-800 transition-colors"
              [matTooltip]="'Koppeling verwijderen'"
              data-cy="linkage-items_delete-button"
              (click)="deleteConfirmation(entry.id)">
              <mat-icon class="text-inherit">delete</mat-icon>
            </button>
          }
        </ng-template>
      </app-table>
    </div>
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatProgressSpinner,
    TableComponent,
    DeleteModuleRightsDirective,
    MatTooltip,
    FuseAlertComponent,
    EntriesLoadErrorAlertComponent,
  ],
})
export class FormlyLinkageItemsComponent extends FieldType implements AfterViewInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #userStore = inject(UserStore);
  readonly #titleService = inject(TitleService);
  readonly #fuseConfirmation = inject(FuseConfirmationService);
  readonly #destroy$ = injectDestroy();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  route = inject(ActivatedRoute);
  paramsParent;
  params = toSignal(this.route.paramMap);

  searchControl = new FormControl();

  readonly #search = new LinkageEntriesSearch({
    entriesService: this.#entriesService,
    destroy$: this.#destroy$,
    namespace: 'LinkageItems',
    searchModule: () => this.searchModule(),
  });
  isSearching = this.#search.isSearching;
  searchResults = this.#search.searchResults;
  loadError = this.#search.loadError;

  readonly #fetchedEntries = signal<Entry[]>([]);
  readonly #hasLoadedEntries = signal(false);
  activeSort = signal<Sort | undefined>(undefined);

  currentModule = computed(() => {
    if (this.paramsParent() === undefined) {
      return this.data['entity'];
    }
    if (this.paramsParent()?.get('name') === 'bedrijfsinstellingen') {
      return this.#modulesStore.enabledModules().find(module => module.slug === 'companies');
    }
    if (this.paramsParent()?.get('name') === 'profiel') {
      return this.#modulesStore.enabledModules().find(module => module.slug === 'users');
    }
    return this.#modulesStore.enabledModules().find(module => module.slug === this.paramsParent()?.get('name'));
  });

  currentEntry = computed(() => {
    if (this.params()?.get('id') === undefined) {
      return this.data['model'];
    }
    if (this.options.formState?.entry != null) {
      return this.options.formState.entry;
    }
    return this.#modulesStore.entries()[this.currentModule()?.id]?.find(entry => {
      return entry.id === this.params()?.get('id');
    });
  });

  linkedModule = computed(() => {
    if (!this.props.linkedModule) return undefined;
    return this.#modulesStore.enabledModules().find(module => module.code === this.props.linkedModule);
  });

  searchModule = computed(() => {
    if (!this.props.searchModuleCode) return undefined;
    return this.#modulesStore.enabledModules().find(module => module.code === this.props.searchModuleCode);
  });

  linkedEntryId = computed(() => {
    if (this.options.formState?.entry?.id != null) {
      return this.options.formState.entry.id;
    } else if (this.#titleService.getTitle().toLowerCase() === 'profiel') {
      return this.#userStore.currentUserId();
    } else if (this.data != undefined && this.data['model']?.id) {
      return this.data['model']?.id;
    } else {
      return this.params()?.get('id');
    }
  });

  // Entries voor display: toont altijd de "andere kant" van de koppeling
  // Once fetchEntries() completes, #fetchedEntries is the single reactive source for all display.
  // Before that, pre-loaded relations provide a fast initial render.
  displayEntries = computed<Entry[]>(() => {
    const searchModuleId = this.searchModule()?.id;
    const entryId = this.linkedEntryId();

    // Once fetched, use the reactive #fetchedEntries signal for all display
    if (this.#hasLoadedEntries()) {
      return this.#fetchedEntries()
        .filter(entry => {
          if (!searchModuleId) return true;
          const otherItem = this.getOtherSideItem(entry);
          return !otherItem?.moduleId || otherItem.moduleId === searchModuleId;
        })
        .map(entry => this.toOtherSideDisplay(entry));
    }

    // Before first fetch: use pre-loaded relations for fast initial render
    const relations = this.currentEntry()?.relations;
    if (relations?.length && searchModuleId && entryId) {
      return relations
        .filter(r => !r.entry?.archived)
        .filter(r => {
          const otherSide = this.getOtherSideFromRelation(r, entryId);
          return otherSide != null && otherSide.moduleId === searchModuleId;
        })
        .map(r => {
          const otherSide = this.getOtherSideFromRelation(r, entryId)!;
          return { id: r.id, selectedInput: otherSide.entry.selectedInput } as Entry;
        });
    }

    return [];
  });

  // IDs of already-linked entries (to prevent duplicate selections)
  readonly #linkedOtherSideIds = computed(() => {
    const relations = this.currentEntry()?.relations;
    const searchModuleId = this.searchModule()?.id;
    const entryId = this.linkedEntryId();

    if (relations?.length && searchModuleId && entryId) {
      return new Set(
        relations
          .filter(r => !r.entry?.archived)
          .map(r => this.getOtherSideFromRelation(r, entryId))
          .filter((item): item is RelationItem => item != null && item.moduleId === searchModuleId)
          .map(item => item.entry.id)
      );
    }

    return new Set(
      this.#fetchedEntries()
        .map(entry => this.getOtherSideEntry(entry)?.id)
        .filter((id): id is string => id != null)
    );
  });

  filteredSearchResults = computed(() => {
    const linkedIds = this.#linkedOtherSideIds();
    return this.searchResults().filter(opt => !linkedIds.has(opt.value.id));
  });

  searchPlaceholder = computed(() => {
    const name = this.searchModule()?.singularName ?? this.searchModule()?.title;
    return name ? `Zoeken naar ${name.toLowerCase()}...` : 'Zoeken...';
  });

  columns = computed<TableColumn[]>(() => {
    const searchMod = this.searchModule();
    const name = searchMod?.singularName ?? searchMod?.title ?? this.props.label;
    const label = name ? `Gekoppelde ${name.toLowerCase()}` : 'Gekoppeld aan';

    return [
      {
        columnName: label,
        id: 'selectedInput',
        type: Types.text,
      },
    ];
  });

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData) {
    super();
    if (this.route?.parent?.paramMap) {
      this.paramsParent = toSignal(this.route?.parent?.paramMap);
    }
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.fetchEntries();
      this.#search.connect(this.searchControl);
    });
  }

  onSearchFocus() {
    if (!this.canMutate()) return;
    if (!this.searchControl.value && this.searchModule()) {
      this.#search.searchOnFocus();
    }
  }

  selectEntry(event: { option: { value: Entry } }) {
    if (!this.canMutate()) return;
    const selectedEntry = event.option.value;
    this.createLinkage(selectedEntry);

    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    // clear() owns the whole reset. The autocomplete already wrote the selected Entry into
    // the control (emitting) before this handler ran; that buffered emission is dropped by
    // the pipeline's control.value guard, so it cannot cancel a focus search started next.
    this.#search.clear();
  }

  onSortChange(sort: Sort) {
    this.activeSort.set(sort);
    this.fetchEntries();
  }

  deleteConfirmation(id: string) {
    if (!this.canMutate()) return;
    const confirmation = this.#fuseConfirmation.open({
      title: this.props.label + ' verwijderen',
      message: 'Weet je zeker dat je deze koppeling wil verwijderen?',
      actions: {
        cancel: { label: 'Annuleren' },
        confirm: { label: 'Verwijderen' },
      },
    });

    confirmation
      .afterClosed()
      .pipe(takeUntil(this.#destroy$))
      .subscribe(result => {
        if (result === 'confirmed' && this.canMutate()) {
          this.#entriesService
            .delete(this.linkedModule()?.id, id)
            .pipe(takeUntil(this.#destroy$))
            .subscribe(() => {
              this.#fetchedEntries.update(entries => entries.filter(e => e.id !== id));
              this.#removeRelationFromParentEntry(id);
            });
        }
      });
  }

  // --- Private methods ---

  private fetchEntries() {
    const fieldA = this.props.linkedFieldA;
    const fieldB = this.props.linkedFieldB;
    if (!fieldA || !fieldB) return;

    const entryId = this.linkedEntryId();
    if (!entryId || !this.linkedModule()?.id) return;

    const filterA = `${UtilityService.dotPathToObjectPath(fieldA + '.id')} eq '${entryId}'`;
    const filterB = `${UtilityService.dotPathToObjectPath(fieldB + '.id')} eq '${entryId}'`;

    const moduleFilterId = this.getModuleFilterId();
    let filter: string;

    if (moduleFilterId) {
      const moduleIdPathA = UtilityService.dotPathToObjectPath(fieldA.split('.').slice(0, -1).join('.') + '.moduleId');
      const moduleIdPathB = UtilityService.dotPathToObjectPath(fieldB.split('.').slice(0, -1).join('.') + '.moduleId');
      filter = `(${filterA} and ${moduleIdPathB} eq '${moduleFilterId}') or (${filterB} and ${moduleIdPathA} eq '${moduleFilterId}')`;
    } else {
      filter = `${filterA} or ${filterB}`;
    }

    this.#entriesService
      .getEntries({
        moduleId: this.linkedModule().id,
        offset: 0,
        limit: 1000,
        filter,
        orderBy: this.activeSort() ?? undefined,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe(entries => {
        this.#fetchedEntries.set(entries);
        this.#hasLoadedEntries.set(true);
      });
  }

  private getModuleFilterId(): string | undefined {
    const defaults = this.props.defaultValuesLinkedItems;
    if (!defaults?.length) return undefined;

    const moduleDefault = defaults.find(item => item.targetField?.endsWith('moduleId'));
    if (!moduleDefault) return undefined;

    switch (moduleDefault.basedOn) {
      case 'moduleId':
        return this.currentModule()?.id;
      case 'static':
        return moduleDefault.value;
      default:
        return undefined;
    }
  }

  /**
   * Determines the "other side" of a Relation based on the current entry ID.
   * If item-1 is the current entry, returns item-2, and vice versa.
   */
  private getOtherSideFromRelation(relation: Relation, currentEntryId: string): RelationItem | undefined {
    if (relation.entry?.['item-1']?.entry?.id === currentEntryId) {
      return relation.entry['item-2'];
    }
    if (relation.entry?.['item-2']?.entry?.id === currentEntryId) {
      return relation.entry['item-1'];
    }
    return undefined;
  }

  /**
   * Transforms a linkage-module entry so both item paths show the "other side".
   * This way the table always shows the linked item, regardless of whether the
   * current entry is on item-1 or item-2.
   */
  private toOtherSideDisplay(linkageEntry: Entry): Entry {
    if (!linkageEntry?.entry) return linkageEntry;

    const fieldAParts = this.props.linkedFieldA?.split('.') ?? [];
    const fieldBParts = this.props.linkedFieldB?.split('.') ?? [];
    const itemAKey = fieldAParts[1]; // bijv. "item-1"
    const itemBKey = fieldBParts[1]; // bijv. "item-2"
    if (!itemAKey || !itemBKey) return linkageEntry;

    const otherSideItem = this.getOtherSideItem(linkageEntry);
    if (!otherSideItem) return linkageEntry;

    return {
      ...linkageEntry,
      selectedInput: otherSideItem.entry?.selectedInput ?? 'Onbekend',
      entry: {
        ...linkageEntry.entry,
        [itemAKey]: otherSideItem,
        [itemBKey]: otherSideItem,
      },
    } as Entry;
  }

  /**
   * Returns the full item object from the "other side"
   * (including moduleId and entry sub-object).
   */
  private getOtherSideItem(
    linkageEntry: Entry
  ): { moduleId?: string; entry?: { id?: string; selectedInput?: string } } | undefined {
    if (!linkageEntry?.entry) return undefined;

    const fieldAParts = this.props.linkedFieldA?.split('.') ?? [];
    const fieldBParts = this.props.linkedFieldB?.split('.') ?? [];
    const itemAKey = fieldAParts[1];
    const itemBKey = fieldBParts[1];

    const itemAPath = fieldAParts.slice(1); // bijv. ["item-1", "entry"]
    const itemBPath = fieldBParts.slice(1); // bijv. ["item-2", "entry"]

    const entryA = this.getNestedValue(linkageEntry.entry, itemAPath);
    const entryB = this.getNestedValue(linkageEntry.entry, itemBPath);

    if (entryA?.id === this.linkedEntryId()) {
      return linkageEntry.entry[itemBKey];
    }
    return linkageEntry.entry[itemAKey];
  }

  /**
   * Returns only the entry sub-object (id + selectedInput) from the "other side".
   */
  private getOtherSideEntry(linkageEntry: Entry): { id?: string; selectedInput?: string } | undefined {
    return this.getOtherSideItem(linkageEntry)?.entry;
  }

  private getNestedValue(obj: any, path: string[]): any {
    return path.reduce((current, key) => current?.[key], obj);
  }

  private createLinkage(selectedEntry: Entry) {
    if (!this.canMutate()) return;
    const linkageModule = this.linkedModule();
    const searchMod = this.searchModule();
    const currentEntry = this.currentEntry();
    if (!linkageModule?.id || !currentEntry?.id || !searchMod) return;

    const fieldA = this.props.linkedFieldA;
    const fieldB = this.props.linkedFieldB;
    if (!fieldA || !fieldB) return;

    const newEntry: Entry = {
      id: undefined,
      entry: { archived: false },
    };

    // Side A: huidige entry
    UtilityService.setKeyValuePath(newEntry, fieldA, {
      id: currentEntry.id,
      selectedInput: currentEntry.selectedInput ?? this.getCurrentEntryDisplayName(),
    });
    const moduleIdPathA = fieldA.split('.').slice(0, -1).join('.') + '.moduleId';
    UtilityService.setKeyValuePath(newEntry, moduleIdPathA, this.currentModule()?.id);

    // Side B: geselecteerde entry
    const selectedLabel = selectedEntry.selectedInput ?? entryDisplayName(selectedEntry, searchMod);
    UtilityService.setKeyValuePath(newEntry, fieldB, {
      id: selectedEntry.id,
      selectedInput: selectedLabel,
    });
    const moduleIdPathB = fieldB.split('.').slice(0, -1).join('.') + '.moduleId';
    UtilityService.setKeyValuePath(newEntry, moduleIdPathB, searchMod.id);

    // Extra default values toepassen
    const defaults = this.props.defaultValuesLinkedItems;
    if (defaults?.length) {
      for (const item of defaults) {
        if (item.targetField?.endsWith('moduleId')) continue;
        let value: any;
        switch (item.basedOn) {
          case 'moduleId':
            value = this.currentModule()?.id;
            break;
          case 'static':
            value = item.value;
            break;
          case 'entry':
            value = item.sourceField ? UtilityService.keyPathToValue(currentEntry, item.sourceField) : undefined;
            break;
        }
        if (value !== undefined) {
          UtilityService.setKeyValuePath(newEntry, item.targetField, value);
        }
      }
    }

    this.#entriesService
      .addEntry(linkageModule.id, newEntry.entry)
      .pipe(
        takeUntil(this.#destroy$),
        catchError(e => {
          console.error('Failed to create linkage:', e);
          return of(null);
        })
      )
      .subscribe(result => {
        if (result) {
          this.#fetchedEntries.update(entries => [...entries, result]);
          this.#addRelationToParentEntry(result);
        }
      });
  }

  private getCurrentEntryDisplayName(): string {
    const entry = this.currentEntry();
    // '' and no id-fallback: a GUID is not a usable label for the current-entry side.
    return entry ? entryDisplayName(entry, this.currentModule(), { fallback: '', idFallback: false }) : '';
  }

  canMutate(): boolean {
    return (
      !this.formControl?.disabled &&
      !this.props.disabled &&
      !this.props.readonly &&
      !this.props['readOnly'] &&
      !this.form?.disabled
    );
  }

  #addRelationToParentEntry(linkageEntry: Entry): void {
    const parentEntry = this.currentEntry();
    const moduleId = this.currentModule()?.id;
    if (!parentEntry?.id || !moduleId) return;

    const newRelation: Relation = {
      id: linkageEntry.id!,
      entry: linkageEntry.entry as Relation['entry'],
    };

    const updatedEntry: Entry = {
      ...parentEntry,
      relations: [...(parentEntry.relations ?? []), newRelation],
    };

    this.#modulesStore.updateEntryState({ entry: updatedEntry, moduleId });
    if (this.options.formState?.entry) {
      this.options.formState.entry = updatedEntry;
    }
  }

  #removeRelationFromParentEntry(linkageEntryId: string): void {
    const parentEntry = this.currentEntry();
    const moduleId = this.currentModule()?.id;
    if (!parentEntry?.id || !moduleId) return;

    const updatedEntry: Entry = {
      ...parentEntry,
      relations: (parentEntry.relations ?? []).filter(r => r.id !== linkageEntryId),
    };

    this.#modulesStore.updateEntryState({ entry: updatedEntry, moduleId });
    if (this.options.formState?.entry) {
      this.options.formState.entry = updatedEntry;
    }
  }
}
